/**
 * Shared poll loop for the async LLM-inference jobs (Spec 3.5).
 *
 * Both `createLlmMetricAdvisor` and `createLlmParameterAdvisor` submit a job
 * (returning a jobId) and then hand that jobId here. This loops a GET on the
 * shared `/infer-jobs/{jobId}` route every ~4s until the job is COMPLETED
 * (returns its free-form `result`) or FAILED (throws). It hides the polling
 * entirely behind a single Promise so the two Step components stay zero-touch —
 * their `advisor.suggest()` simply resolves later (see the deliberate divergence
 * from the UAT RTK-Query polling in the spec).
 *
 * Design notes:
 * - **Wall-clock timeout (~90s) lives INSIDE the loop**, not an AbortController:
 *   the poll is many short fetches, so we abort the LOOP, not any single fetch.
 * - **A 404 on the first tick(s) is tolerated** — the background Lambda writes
 *   the initial S3 record asynchronously, so the job may not be readable on the
 *   very first GET. We treat 404 as "still IN_PROGRESS" and keep polling until
 *   the wall-clock guard fires.
 * - The poll fn, sleep, and clock are injectable so tests run deterministically
 *   with mocked responses and no real timers.
 */

import { getInferJob, OnboardingApiError } from './onboardingApi';
import { InferJobStatusResponse } from './types';

/** Default cadence between poll ticks (ms). */
const DEFAULT_POLL_INTERVAL_MS = 4000;
/** Default wall-clock budget before giving up (ms). */
const DEFAULT_TIMEOUT_MS = 90_000;

export interface PollInferJobDeps {
  /** Injectable poll client; defaults to the real getInferJob. */
  getInferJobFn?: (jobId: string) => Promise<InferJobStatusResponse>;
  /** Injectable sleep; defaults to a real setTimeout-based delay. */
  sleepFn?: (ms: number) => Promise<void>;
  /** Injectable clock; defaults to Date.now. */
  nowFn?: () => number;
  /** Poll cadence in ms (default 4000). */
  intervalMs?: number;
  /** Wall-clock budget in ms (default 90000). */
  timeoutMs?: number;
}

function realSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Polls `jobId` until the job is COMPLETED (resolves with its `result`) or
 * FAILED / times out (rejects with an OnboardingApiError). `result` is returned
 * as `unknown`; each advisor casts it to its own response type.
 */
export async function pollInferJob(jobId: string, deps: PollInferJobDeps = {}): Promise<unknown> {
  const poll = deps.getInferJobFn ?? getInferJob;
  const sleep = deps.sleepFn ?? realSleep;
  const now = deps.nowFn ?? Date.now;
  const intervalMs = deps.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const timeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const start = now();

  // Loop until a terminal status or the wall-clock guard fires.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let job: InferJobStatusResponse;
    try {
      job = await poll(jobId);
    } catch (err) {
      // Tolerate a 404 (job record not yet written) — keep polling until the
      // wall-clock guard. Any other error is real and propagates.
      if (err instanceof OnboardingApiError && err.statusCode === 404) {
        job = { jobId, jobType: 'METRICS', status: 'IN_PROGRESS' };
      } else {
        throw err;
      }
    }

    if (job.status === 'COMPLETED') {
      return job.result;
    }
    if (job.status === 'FAILED') {
      const message = job.error ?? 'Inference job failed';
      throw new OnboardingApiError(502, message, job.code ?? 'LLM_ERROR');
    }

    // Still IN_PROGRESS — enforce the wall-clock budget before sleeping again.
    if (now() - start >= timeoutMs) {
      throw new OnboardingApiError(
        504,
        `Inference job ${jobId} did not complete within ${Math.round(timeoutMs / 1000)}s`,
        'POLL_TIMEOUT',
      );
    }
    await sleep(intervalMs);
  }
}
